import { motion } from "framer-motion";
import { PackageOpen } from "lucide-react";
import { ChapterShell } from "../ChapterShell";
import { FadeUp } from "../Reveal";

const JOURNEY = ["Awareness", "Symptoms", "Information", "Doctor Visit", "Diagnosis", "Treatment", "Follow-up"];

const SMART_PACK = ["Digital Ecosystem", "Patient Education", "Smart Packaging", "Connected Care"];

export const ChapterSeven = () => (
  <ChapterShell
    n="07"
    id="chapter-7"
    era="Patient First"
    title="Patient-Centric Marketing"
    quote="The best marketing journey starts with the patient."
  >
    <FadeUp className="glass inline-block rounded-2xl px-8 py-5">
      <span className="font-display text-3xl font-black tracking-tight text-[#0A192F] md:text-4xl">
        PATIENT<span className="text-[#00BFA5]">.</span>
      </span>
    </FadeUp>

    <div className="mt-12 flex flex-col gap-4 md:flex-row md:items-center">
      {JOURNEY.map((step, i) => (
        <motion.div
          key={step}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true, margin: "-40px" }}
          transition={{ duration: 0.5, delay: i * 0.11 }}
          className="flex items-center md:flex-1"
        >
          <span className="whitespace-nowrap rounded-full bg-[#0A192F]/[0.05] px-5 py-2.5 font-display text-[11px] font-bold uppercase tracking-[0.14em] text-[#0A192F]">
            {step}
          </span>
          {i < JOURNEY.length - 1 && (
            <span className="mx-2 hidden h-px flex-1 bg-gradient-to-r from-[#00BFA5] to-[#00E5FF] md:block" />
          )}
        </motion.div>
      ))}
    </div>

    <FadeUp delay={0.15} className="mt-8 max-w-2xl text-sm leading-relaxed text-[#4A5568] md:text-base">
      Driving patient awareness programs and increasing patient footfalls to doctors' clinics — from camps and TV
      talks to radio and press, meeting patients wherever they are.
    </FadeUp>

    <div className="mt-16 grid grid-cols-1 items-center gap-12 lg:grid-cols-12">
      <div className="lg:col-span-5" style={{ perspective: 1200 }}>
        <motion.div
          initial={{ rotateY: -24, opacity: 0 }}
          whileInView={{ rotateY: 0, opacity: 1 }}
          viewport={{ once: true, margin: "-80px" }}
          transition={{ duration: 1, ease: [0.16, 1, 0.3, 1] }}
          whileHover={{ rotateY: 10, y: -6 }}
          className="relative mx-auto max-w-xs"
        >
          <div className="absolute -inset-6 rounded-[2rem] bg-gradient-to-br from-[#00BFA5]/25 to-[#00E5FF]/20 blur-xl" />
          <div className="glass relative rounded-[1.8rem] p-8 text-center">
            <PackageOpen size={30} className="mx-auto text-[#00BFA5]" />
            <p className="mt-4 font-display text-3xl font-black tracking-tight text-[#0A192F]">Smart Pack</p>
            <p className="mt-2 text-xs font-semibold uppercase tracking-[0.2em] text-[#0A192F]/50">
              Packaging, reimagined
            </p>
          </div>
        </motion.div>
      </div>
      <div className="lg:col-span-7">
        <p className="max-w-xl text-sm leading-relaxed text-[#4A5568] md:text-base">
          Repositioned products using insights from consumer research data — and opened the pack into a digital
          ecosystem with the <strong className="text-[#0A192F]">"Smart Pack"</strong> concept.
        </p>
        <div className="mt-8 flex flex-wrap gap-3">
          {SMART_PACK.map((c, i) => (
            <motion.span
              key={c}
              initial={{ opacity: 0, scale: 0.8 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true, margin: "-40px" }}
              transition={{ duration: 0.5, delay: 0.3 + i * 0.12, type: "spring" }}
              className="glass rounded-full px-5 py-2.5 text-xs font-bold uppercase tracking-[0.16em] text-[#0A192F]"
            >
              {c}
            </motion.span>
          ))}
        </div>
      </div>
    </div>
  </ChapterShell>
);
